import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Send, Users, Inbox, Mail, Plus, Download, Image as ImageIcon, Save, Search, X, BarChart3, DollarSign, Upload, RefreshCw, MessageCircle, Store, Gavel } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api, adminHeaders, getAdminKey, setAdminKey, clearAdminKey } from "@/lib/api";

const inputCls = "w-full bg-white border border-navy/20 px-4 py-3 text-navy placeholder:text-navy/40 focus:outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors";

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(", ") || fallback;
  return fallback;
};

const STATUSES = ["new", "reviewed", "quoted", "paid", "closed"];
const STATUS_STYLES = {
  new: "bg-emerald/10 text-emerald",
  reviewed: "bg-blue-100 text-blue-700",
  quoted: "bg-amber-100 text-amber-700",
  paid: "bg-green-100 text-green-700",
  closed: "bg-navy/10 text-navy/60",
};

const PRICE_TEMPLATE = {
  subject: "SAYE IMPEX — This Week's Buying Prices",
  body: `We're actively buying and paying competitive rates this week:

• LDPE / LLDPE film — top dollar for clean, baled stock lots
• HDPE regrind & bottles — premium pricing
• PP stock lots & off-grade pellets — strong rates
• Purge & production stock lots — mixed loads welcome

FTL & FCL pickups available. Fast payment on collection.

Reply to this email or call +1-778-987-7884 to schedule a pickup or get a quote for your material.`,
};

const Login = ({ onLogin }) => {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { key });
      setAdminKey(key);
      onLogin();
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid admin key.");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-navy grain flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm relative z-10">
        <div className="font-display font-black text-3xl text-white tracking-tight">SAYE</div>
        <div className="font-mono text-xs tracking-[0.25em] text-emerald mb-10">ADMIN PORTAL</div>
        <input className={inputCls} type="password" placeholder="Admin key" value={key} onChange={(e) => setKey(e.target.value)} data-testid="admin-key-input" />
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="w-full mt-4 bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-7 py-4 hover:bg-white transition-colors disabled:opacity-50">
          {loading ? "Verifying..." : "Enter Dashboard"}
        </button>
        <Link to="/" className="block mt-6 font-mono text-xs text-white/40 hover:text-emerald transition-colors">← Back to site</Link>
      </form>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white border border-navy/10 p-6 flex items-center justify-between" data-testid={`stat-${label}`}>
    <div>
      <div className="font-display font-black text-4xl text-navy tracking-tight">{value}</div>
      <div className="font-mono text-xs uppercase tracking-widest text-navy/50 mt-1">{label}</div>
    </div>
    <Icon className="text-emerald" size={28} strokeWidth={1.5} />
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [stats, setStats] = useState({ offers: 0, new_offers: 0, clients: 0, campaigns: 0 });
  const [offers, setOffers] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [newClient, setNewClient] = useState({ name: "", email: "", company: "", phone: "" });
  const [camp, setCamp] = useState({ subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [offerSearch, setOfferSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [attView, setAttView] = useState(null); // {offer, images}
  const [analytics, setAnalytics] = useState({ by_material: [], by_status: [] });
  const [quote, setQuote] = useState(null); // {offer, price, message}
  const [quoting, setQuoting] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [listings, setListings] = useState([]);
  const emptyListing = { title: "", material_type: "", description: "", quantity: "", location: "", images: [] };
  const [newListing, setNewListing] = useState(emptyListing);
  const [creatingListing, setCreatingListing] = useState(false);
  const [editListing, setEditListing] = useState(null); // full listing object being edited
  const [savingEdit, setSavingEdit] = useState(false);
  const [bidsView, setBidsView] = useState(null); // {listing, bids}

  const load = useCallback(async () => {
    try {
      const [s, o, c, cp, t, a, q, lg] = await Promise.all([
        api.get("/stats", adminHeaders()),
        api.get("/offers", adminHeaders()),
        api.get("/clients", adminHeaders()),
        api.get("/campaigns", adminHeaders()),
        api.get("/templates", adminHeaders()),
        api.get("/analytics", adminHeaders()),
        api.get("/quotes", adminHeaders()),
        api.get("/admin/listings", adminHeaders()),
      ]);
      setStats(s.data); setOffers(o.data); setClients(c.data); setCampaigns(cp.data);
      setTemplates(t.data); setAnalytics(a.data); setQuotes(q.data); setListings(lg.data);
    } catch { toast.error("Failed to load data."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onListingImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const tooBig = files.find((f) => f.size > 2.5 * 1024 * 1024);
    if (tooBig) { toast.error("Each image must be under 2.5MB."); return; }
    const readAsDataUrl = (f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f);
    });
    const dataUrls = await Promise.all(files.map(readAsDataUrl));
    setNewListing((p) => ({ ...p, images: [...(p.images || []), ...dataUrls].slice(0, 8) }));
    e.target.value = "";
  };

  const createListing = async (e) => {
    e.preventDefault();
    if (!newListing.title || !newListing.material_type) { toast.error("Title and material are required."); return; }
    setCreatingListing(true);
    try {
      const payload = { ...newListing, image: newListing.images?.[0] || "" };
      await api.post("/listings", payload, adminHeaders());
      toast.success("Listing published.");
      setNewListing(emptyListing);
      load();
    } catch { toast.error("Failed to publish listing."); }
    finally { setCreatingListing(false); }
  };

  const toggleListing = async (l) => {
    // Cycle: open → sold → closed → open
    const next = l.status === "open" ? "sold" : l.status === "sold" ? "closed" : "open";
    try {
      await api.patch(`/listings/${l.id}`, { status: next }, adminHeaders());
      toast.success(`Listing marked ${next.toUpperCase()}.`);
      load();
    } catch { toast.error("Failed to update listing."); }
  };

  const deleteListing = async (id) => {
    try { await api.delete(`/listings/${id}`, adminHeaders()); toast.success("Listing deleted."); load(); }
    catch { toast.error("Failed to delete."); }
  };

  const openEditListing = (l) => {
    // Normalise: if listing has only legacy `image` and no `images`, seed the gallery.
    const images = (l.images && l.images.length) ? l.images : (l.image ? [l.image] : []);
    setEditListing({ ...l, images });
  };

  const onEditListingImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const tooBig = files.find((f) => f.size > 2.5 * 1024 * 1024);
    if (tooBig) { toast.error("Each image must be under 2.5MB."); return; }
    const readAsDataUrl = (f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f);
    });
    const dataUrls = await Promise.all(files.map(readAsDataUrl));
    setEditListing((p) => ({ ...p, images: [...(p.images || []), ...dataUrls].slice(0, 8) }));
    e.target.value = "";
  };

  const removeEditImage = (idx) => setEditListing((p) => ({ ...p, images: (p.images || []).filter((_, i) => i !== idx) }));

  const saveEditListing = async (e) => {
    e.preventDefault();
    if (!editListing?.title || !editListing?.material_type) { toast.error("Title and material are required."); return; }
    setSavingEdit(true);
    try {
      const { id, title, material_type, description, quantity, location, images, status } = editListing;
      const payload = { title, material_type, description, quantity, location, images: images || [], image: (images && images[0]) || "", status };
      await api.patch(`/listings/${id}`, payload, adminHeaders());
      toast.success("Listing updated.");
      setEditListing(null);
      load();
    } catch { toast.error("Failed to update listing."); }
    finally { setSavingEdit(false); }
  };

  const viewBids = async (listing) => {
    try {
      const res = await api.get(`/listings/${listing.id}/bids`, adminHeaders());
      setBidsView({ listing, bids: res.data });
    } catch { toast.error("Failed to load bids."); }
  };

  const importClients = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const res = await api.post("/clients/import", { csv: text }, adminHeaders());
      const d = res.data;
      toast.success(`Imported ${d.added} client${d.added === 1 ? "" : "s"}. Skipped ${d.skipped} duplicate${d.skipped === 1 ? "" : "s"}${d.error_count ? `, ${d.error_count} invalid` : ""}.`);
      load();
    } catch { toast.error("Import failed. Check the CSV format."); }
    e.target.value = "";
  };

  const retryQuote = async (id) => {
    try {
      const res = await api.post(`/quotes/${id}/retry`, {}, adminHeaders());
      toast.success(res.data.email_sent ? "Quote re-sent successfully." : "Retry failed — email still undelivered.");
      load();
    } catch { toast.error("Retry failed."); }
  };

  const sendQuote = async () => {
    if (!quote.price) { toast.error("Enter a price."); return; }
    setQuoting(true);
    try {
      const res = await api.post(`/offers/${quote.offer.id}/quote`, { price: quote.price, message: quote.message }, adminHeaders());
      toast.success(res.data.email_sent ? "Quote emailed to supplier." : "Quote saved (email could not be delivered).");
      setQuote(null);
      load();
    } catch { toast.error("Failed to send quote."); }
    finally { setQuoting(false); }
  };

  const saveTemplate = async () => {
    if (!tplName || !camp.subject || !camp.body) { toast.error("Add a name, subject and body first."); return; }
    if (templates.some((t) => t.name.trim().toLowerCase() === tplName.trim().toLowerCase())) {
      toast.error("A template with this name already exists."); return;
    }
    try {
      await api.post("/templates", { name: tplName, subject: camp.subject, body: camp.body }, adminHeaders());
      toast.success("Template saved.");
      setTplName("");
      load();
    } catch { toast.error("Failed to save template."); }
  };
  const delTemplate = async (id) => {
    try { await api.delete(`/templates/${id}`, adminHeaders()); toast.success("Template deleted."); load(); }
    catch { toast.error("Failed to delete."); }
  };

  const exportCsv = async (kind) => {
    try {
      const res = await api.get(`/export/${kind}.csv`, { ...adminHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `saye_${kind}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch { toast.error("Export failed."); }
  };

  const viewAttachments = async (offer) => {
    try {
      const res = await api.get(`/offers/${offer.id}/attachments`, adminHeaders());
      setAttView({ offer, images: res.data });
    } catch { toast.error("Failed to load photos."); }
  };

  const addClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || (!newClient.email && !newClient.phone)) { toast.error("Name and an email or WhatsApp phone are required."); return; }
    if (newClient.email && clients.some((c) => c.email && c.email.trim().toLowerCase() === newClient.email.trim().toLowerCase())) {
      toast.error("A client with this email already exists."); return;
    }
    try {
      await api.post("/clients", newClient, adminHeaders());
      toast.success("Client added.");
      setNewClient({ name: "", email: "", company: "", phone: "" });
      load();
    } catch (err) { toast.error(errText(err, "Failed to add client.")); }
  };

  const delClient = async (id) => {
    try { await api.delete(`/clients/${id}`, adminHeaders()); toast.success("Client removed."); load(); }
    catch { toast.error("Failed to remove."); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/offers/${id}?status=${status}`, {}, adminHeaders()); toast.success(`Marked as ${status}.`); load(); }
    catch { toast.error("Failed to update status."); }
  };

  const toggleClient = (id) => {
    setSelectedClients((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const allSelected = clients.length > 0 && selectedClients.length === clients.length;
  const toggleAll = () => setSelectedClients(allSelected ? [] : clients.map((c) => c.id));

  const sendCampaign = async (e) => {
    e.preventDefault();
    if (!camp.subject || !camp.body) { toast.error("Subject and message required."); return; }
    if (clients.length === 0) { toast.error("Add at least one client first."); return; }
    setSending(true);
    try {
      const payload = { ...camp, client_ids: selectedClients.length ? selectedClients : null };
      const res = await api.post("/campaigns/send", payload, adminHeaders());
      toast.success(`Sent to ${res.data.sent} of ${res.data.total} clients.`);
      setCamp({ subject: "", body: "" });
      setSelectedClients([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed."); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-offwhite" data-testid="admin-dashboard">
      <header className="bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-display font-black text-2xl text-white tracking-tight">SAYE</span>
            <span className="font-mono text-xs tracking-[0.25em] text-emerald">ADMIN</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="font-mono text-xs text-white/60 hover:text-emerald transition-colors">View Site</Link>
            <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 font-mono text-xs text-white hover:text-emerald transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <StatCard label="Total Offers" value={stats.offers} icon={Inbox} />
          <StatCard label="New Offers" value={stats.new_offers} icon={Inbox} />
          <StatCard label="Clients" value={stats.clients} icon={Users} />
          <StatCard label="Campaigns" value={stats.campaigns} icon={Mail} />
          <StatCard label="Live Listings" value={stats.listings || 0} icon={Store} />
          <StatCard label="Bids" value={stats.bids || 0} icon={Gavel} />
        </div>

        <Tabs defaultValue="offers">
          <TabsList className="bg-white border border-navy/10 rounded-none p-0 h-auto flex-wrap">
            <TabsTrigger value="offers" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-offers">Offers</TabsTrigger>
            <TabsTrigger value="listings" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-listings">Listings</TabsTrigger>
            <TabsTrigger value="clients" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="send" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-send">Send Offers</TabsTrigger>
            <TabsTrigger value="history" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-history">History</TabsTrigger>
            <TabsTrigger value="insights" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-insights">Insights</TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-navy data-[state=active]:text-white px-6 py-3" data-testid="tab-quotes">Quotes</TabsTrigger>
          </TabsList>

          {/* OFFERS */}
          <TabsContent value="offers" className="mt-6">
            <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between mb-4">
              <div className="flex gap-3 flex-1">
                <div className="relative flex-1 max-w-sm">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
                  <input value={offerSearch} onChange={(e) => setOfferSearch(e.target.value)} placeholder="Search company, contact, material..." data-testid="offer-search"
                    className="w-full bg-white border border-navy/20 pl-9 pr-4 py-2.5 text-navy text-sm placeholder:text-navy/40 focus:outline-none focus:border-emerald" />
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="offer-status-filter"
                  className="bg-white border border-navy/20 px-3 py-2.5 text-navy text-sm font-mono uppercase tracking-wide focus:outline-none focus:border-emerald">
                  <option value="all">All statuses</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <button onClick={() => exportCsv("offers")} data-testid="export-offers-btn"
                className="flex items-center gap-2 border border-navy/20 text-navy font-mono text-xs uppercase tracking-widest px-4 py-2.5 hover:border-emerald hover:text-emerald transition-colors">
                <Download size={14} /> Export CSV
              </button>
            </div>
            {(() => {
              const q = offerSearch.trim().toLowerCase();
              const filtered = offers.filter((o) =>
                (statusFilter === "all" || o.status === statusFilter) &&
                (!q || `${o.company_name} ${o.contact_name} ${o.material_type} ${o.email}`.toLowerCase().includes(q)));
              return (
                <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="offers-table">
                  {filtered.length === 0 ? <p className="p-8 text-navy/50 font-mono text-sm">No matching offers.</p> : (
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-navy/10 text-left font-mono text-xs uppercase tracking-widest text-navy/50">
                        <th className="p-4">Company</th><th className="p-4">Contact</th><th className="p-4">Material</th><th className="p-4">Qty</th><th className="p-4">Price</th><th className="p-4">Photos</th><th className="p-4">Status</th><th className="p-4">Date</th><th className="p-4">Action</th>
                      </tr></thead>
                      <tbody>
                        {filtered.map((o) => (
                          <tr key={o.id} className="border-b border-navy/5 hover:bg-offwhite" data-testid={`offer-row-${o.id}`}>
                            <td className="p-4 font-semibold text-navy">{o.company_name}<div className="font-normal text-navy/50 text-xs">{o.email}</div></td>
                            <td className="p-4 text-navy/70">{o.contact_name}<div className="text-navy/40 text-xs">{o.phone}</div></td>
                            <td className="p-4"><Badge className="rounded-none bg-emerald/10 text-emerald hover:bg-emerald/10 border-0">{o.material_type}</Badge></td>
                            <td className="p-4 text-navy/70">{o.quantity}</td>
                            <td className="p-4 text-navy/70">{o.price || "—"}</td>
                            <td className="p-4">
                              {o.attachment_count > 0 ? (
                                <button onClick={() => viewAttachments(o)} data-testid={`view-photos-${o.id}`}
                                  className="flex items-center gap-1 text-emerald hover:underline font-mono text-xs">
                                  <ImageIcon size={14} /> {o.attachment_count}
                                </button>
                              ) : <span className="text-navy/30 font-mono text-xs">—</span>}
                            </td>
                            <td className="p-4">
                              <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} data-testid={`offer-status-${o.id}`}
                                className={`font-mono text-xs uppercase tracking-wide px-2 py-1 border-0 focus:outline-none cursor-pointer ${STATUS_STYLES[o.status] || STATUS_STYLES.new}`}>
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                            <td className="p-4 text-navy/50 font-mono text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                            <td className="p-4">
                              <button onClick={() => setQuote({ offer: o, price: o.quoted_price || "", message: "" })} data-testid={`quote-btn-${o.id}`}
                                className="flex items-center gap-1 border border-navy/20 text-navy font-mono text-xs uppercase tracking-wide px-3 py-1.5 hover:bg-emerald hover:border-emerald transition-colors">
                                <DollarSign size={12} /> Quote
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* LISTINGS */}
          <TabsContent value="listings" className="mt-6 grid md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <form onSubmit={createListing} className="bg-white border border-navy/10 p-6 space-y-4" data-testid="add-listing-form">
                <div className="font-mono text-xs uppercase tracking-widest text-navy/50">Publish a Listing</div>
                <input className={inputCls} placeholder="Title * (e.g. Clean HDPE Regrind 40,000 lbs)" value={newListing.title} onChange={(e) => setNewListing({ ...newListing, title: e.target.value })} data-testid="listing-title" />
                <select className={inputCls} value={newListing.material_type} onChange={(e) => setNewListing({ ...newListing, material_type: e.target.value })} data-testid="listing-material">
                  <option value="">Material *</option>
                  <optgroup label="Films, Rolls & Sheets">
                    <option value="LDPE Film Rolls">LDPE Film Rolls</option>
                    <option value="PET Clear Film Rolls">PET Clear Film Rolls</option>
                    <option value="BOPP Clear Film Rolls">BOPP Clear Film Rolls</option>
                    <option value="PP Film Rolls">PP Film Rolls</option>
                    <option value="Polyester Film Rolls">Polyester Film Rolls</option>
                    <option value="Metallised PET Rolls">Metallised PET Rolls</option>
                    <option value="Printed BOPP / PET Rolls">Printed BOPP / PET Rolls</option>
                    <option value="Printed Laminate Rolls">Printed Laminate Rolls</option>
                    <option value="Unprinted Laminate Rolls">Unprinted Laminate Rolls</option>
                    <option value="PEPA LDPE Nylon Rolls">PEPA LDPE Nylon Rolls</option>
                    <option value="EVOH Rolls">EVOH Rolls</option>
                    <option value="PP Woven (Lumber Wrap) Rolls">PP Woven (Lumber Wrap) Rolls</option>
                    <option value="PP Non-Woven Rolls">PP Non-Woven Rolls</option>
                    <option value="PVC Rolls">PVC Rolls</option>
                    <option value="Rubber Rolls">Rubber Rolls</option>
                    <option value="Vinyl Flooring Rolls">Vinyl Flooring Rolls</option>
                    <option value="Stamping Foil Rolls — Unused">Stamping Foil Rolls — Unused</option>
                    <option value="Stamping Foil Rolls — Used">Stamping Foil Rolls — Used</option>
                    <option value="PC Sheets Cut-offs">PC Sheets Cut-offs</option>
                    <option value="Acrylic Sheets Cut-offs">Acrylic Sheets Cut-offs</option>
                    <option value="Mixed Lots of Rolls">Mixed Lots of Rolls</option>
                  </optgroup>
                  <optgroup label="Polyolefins">
                    <option value="LDPE">LDPE — Low-Density Polyethylene</option>
                    <option value="LLDPE">LLDPE — Linear Low-Density Polyethylene</option>
                    <option value="HDPE">HDPE — High-Density Polyethylene</option>
                    <option value="PP">PP — Polypropylene</option>
                    <option value="EVA">EVA — Ethylene Vinyl Acetate</option>
                  </optgroup>
                  <optgroup label="Styrenics">
                    <option value="PS">PS — Polystyrene</option>
                    <option value="HIPS">HIPS — High-Impact Polystyrene</option>
                    <option value="EPS">EPS — Expanded Polystyrene</option>
                    <option value="ABS">ABS — Acrylonitrile Butadiene Styrene</option>
                    <option value="SAN">SAN — Styrene Acrylonitrile</option>
                  </optgroup>
                  <optgroup label="Engineering Plastics">
                    <option value="PC">PC — Polycarbonate</option>
                    <option value="PA">PA — Polyamide / Nylon</option>
                    <option value="POM">POM — Polyoxymethylene / Acetal</option>
                    <option value="PBT">PBT — Polybutylene Terephthalate</option>
                    <option value="PPS">PPS — Polyphenylene Sulfide</option>
                    <option value="PEEK">PEEK — Polyether Ether Ketone</option>
                    <option value="PMMA">PMMA / Acrylic — Polymethyl Methacrylate</option>
                    <option value="PTFE">PTFE — Teflon</option>
                  </optgroup>
                  <optgroup label="PVC & Halogenated">
                    <option value="PVC">PVC — Polyvinyl Chloride</option>
                    <option value="CPVC">CPVC — Chlorinated PVC</option>
                  </optgroup>
                  <optgroup label="PET & Films">
                    <option value="PET">PET / PETE — Polyethylene Terephthalate</option>
                    <option value="PETG">PETG — Glycol-Modified PET</option>
                    <option value="BOPP">BOPP — Biaxially Oriented Polypropylene</option>
                    <option value="CPP">CPP — Cast Polypropylene</option>
                  </optgroup>
                  <optgroup label="Polyurethane & Elastomers">
                    <option value="PU">PU / PUR — Polyurethane</option>
                    <option value="TPU">TPU — Thermoplastic Polyurethane</option>
                    <option value="TPE">TPE — Thermoplastic Elastomer</option>
                  </optgroup>
                  <optgroup label="Rubber">
                    <option value="Rubber">Rubber (General)</option>
                    <option value="EPDM">EPDM</option>
                    <option value="SBR">SBR — Styrene Butadiene Rubber</option>
                    <option value="Nitrile">Nitrile</option>
                    <option value="Silicone">Silicone</option>
                  </optgroup>
                  <optgroup label="Paper & Board">
                    <option value="OCC">OCC — Old Corrugated Cardboard</option>
                    <option value="Paper">Paper</option>
                    <option value="Kraft Paper">Kraft Paper</option>
                    <option value="PEPA">PEPA — Poly-Coated Paper</option>
                    <option value="Tetra Pak">Tetra Pak</option>
                    <option value="Laminate">Laminate</option>
                    <option value="Foil">Foil / Stamping Foil</option>
                  </optgroup>
                  <optgroup label="Medical / PPE">
                    <option value="PPE">PPE (General)</option>
                    <option value="Non-Woven PP">Non-Woven PP</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="Mixed">Mixed / Combination Load</option>
                    <option value="Off-Grade Pellets">Off-Grade Pellets</option>
                    <option value="Purge">Purge</option>
                    <option value="Regrind">Regrind (Mixed)</option>
                    <option value="Other">Other</option>
                  </optgroup>
                </select>
                <textarea className={`${inputCls} min-h-[90px] resize-none`} placeholder="Description" value={newListing.description} onChange={(e) => setNewListing({ ...newListing, description: e.target.value })} data-testid="listing-description" />
                <input className={inputCls} placeholder="Quantity available" value={newListing.quantity} onChange={(e) => setNewListing({ ...newListing, quantity: e.target.value })} data-testid="listing-quantity" />
                <input className={inputCls} placeholder="Location" value={newListing.location} onChange={(e) => setNewListing({ ...newListing, location: e.target.value })} data-testid="listing-location" />
                <label className="flex items-center gap-2 border border-dashed border-navy/30 px-4 py-3 cursor-pointer hover:border-emerald transition-colors text-navy/70 text-sm" data-testid="listing-image-label">
                  <Upload size={16} className="text-emerald" /> {(newListing.images?.length || 0) > 0 ? `${newListing.images.length} photo(s) added — add more` : "Add photos (optional, up to 8)"}
                  <input type="file" accept="image/*" multiple onChange={onListingImage} className="hidden" data-testid="listing-image-input" />
                </label>
                {(newListing.images?.length || 0) > 0 && (
                  <div className="grid grid-cols-4 gap-2" data-testid="new-listing-image-previews">
                    {newListing.images.map((src, i) => (
                      <div key={i} className="relative aspect-square border border-navy/10">
                        <img src={src} alt={`preview-${i}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setNewListing((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))}
                          data-testid={`remove-new-image-${i}`}
                          className="absolute -top-2 -right-2 bg-navy text-white w-5 h-5 flex items-center justify-center hover:bg-red-500 transition-colors">
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="submit" disabled={creatingListing} data-testid="publish-listing-btn" className="w-full bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-6 py-3 flex items-center justify-center gap-2 hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
                  <Plus size={16} /> {creatingListing ? "Publishing..." : "Publish Listing"}
                </button>
              </form>
            </div>
            <div className="md:col-span-8">
              <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="listings-table">
                {listings.length === 0 ? <p className="p-8 text-navy/50 font-mono text-sm">No listings yet. Publish one to show it on your public Offers page.</p> : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-navy/10 text-left font-mono text-xs uppercase tracking-widest text-navy/50">
                      <th className="p-4">Listing</th><th className="p-4">Material</th><th className="p-4">Bids</th><th className="p-4">Status</th><th className="p-4">Actions</th>
                    </tr></thead>
                    <tbody>
                      {listings.map((l) => (
                        <tr key={l.id} className="border-b border-navy/5 hover:bg-offwhite" data-testid={`listing-row-${l.id}`}>
                          <td className="p-4 font-semibold text-navy">{l.title}<div className="font-normal text-navy/40 text-xs">{l.quantity}</div></td>
                          <td className="p-4"><Badge className="rounded-none bg-emerald/10 text-emerald hover:bg-emerald/10 border-0">{l.material_type}</Badge></td>
                          <td className="p-4">
                            <button onClick={() => viewBids(l)} data-testid={`view-bids-${l.id}`} className="flex items-center gap-1 text-emerald hover:underline font-mono text-xs">
                              <Gavel size={14} /> {l.bid_count || 0}
                            </button>
                          </td>
                          <td className="p-4">
                            <button onClick={() => toggleListing(l)} data-testid={`toggle-listing-${l.id}`}
                              title="Click to cycle: open → sold → closed → open"
                              className={`font-mono text-xs uppercase tracking-wide px-2 py-1 ${l.status === "open" ? "bg-green-100 text-green-700" : l.status === "sold" ? "bg-navy text-white" : "bg-navy/10 text-navy/60"}`}>
                              {l.status}
                            </button>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {l.status !== "sold" && (
                                <button onClick={() => api.patch(`/listings/${l.id}`, { status: "sold" }, adminHeaders()).then(() => { toast.success("Marked SOLD."); load(); }).catch(() => toast.error("Failed."))}
                                  data-testid={`mark-sold-${l.id}`} title="Mark this listing as SOLD"
                                  className="font-mono text-[10px] uppercase tracking-widest bg-navy text-white px-2 py-1 hover:bg-emerald hover:text-navy transition-colors">
                                  Mark Sold
                                </button>
                              )}
                              <button onClick={() => openEditListing(l)} data-testid={`edit-listing-${l.id}`}
                                className="text-navy/50 hover:text-emerald transition-colors" title="Edit listing"><Save size={16} /></button>
                              <button onClick={() => deleteListing(l.id)} data-testid={`del-listing-${l.id}`}
                                className="text-navy/30 hover:text-red-500 transition-colors" title="Delete listing"><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="clients" className="mt-6 grid md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <form onSubmit={addClient} className="bg-white border border-navy/10 p-6 space-y-4" data-testid="add-client-form">
                <div className="font-mono text-xs uppercase tracking-widest text-navy/50">Add Client</div>
                <input className={inputCls} placeholder="Name *" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="client-name" />
                <input className={inputCls} type="email" placeholder="Email (optional if phone given)" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} data-testid="client-email" />
                <input className={inputCls} placeholder="Company" value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} data-testid="client-company" />
                <input className={inputCls} placeholder="WhatsApp phone (e.g. +1 647 685 2707)" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="client-phone" />
                <button type="submit" data-testid="add-client-btn" className="w-full bg-navy text-white font-mono text-xs uppercase tracking-widest px-6 py-3 flex items-center justify-center gap-2 hover:bg-emerald hover:text-navy transition-colors">
                  <Plus size={16} /> Add Client
                </button>
              </form>
            </div>
            <div className="md:col-span-8">
              <div className="flex justify-end gap-3 mb-3">
                <label className="flex items-center gap-2 border border-navy/20 text-navy font-mono text-xs uppercase tracking-widest px-4 py-2.5 hover:border-emerald hover:text-emerald transition-colors cursor-pointer" data-testid="import-clients-label">
                  <Upload size={14} /> Import CSV
                  <input type="file" accept=".csv,text/csv" onChange={importClients} className="hidden" data-testid="import-clients-input" />
                </label>
                <button onClick={() => exportCsv("clients")} data-testid="export-clients-btn"
                  className="flex items-center gap-2 border border-navy/20 text-navy font-mono text-xs uppercase tracking-widest px-4 py-2.5 hover:border-emerald hover:text-emerald transition-colors">
                  <Download size={14} /> Export CSV
                </button>
              </div>
              <p className="text-navy/40 font-mono text-[11px] mb-3 text-right">CSV columns: name, phone, email, company — email OR phone is enough (WhatsApp-only lists supported).</p>
              <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="clients-table">
                {clients.length === 0 ? <p className="p-8 text-navy/50 font-mono text-sm">No clients yet.</p> : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-navy/10 text-left font-mono text-xs uppercase tracking-widest text-navy/50">
                      <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Phone</th><th className="p-4">Company</th><th className="p-4"></th>
                    </tr></thead>
                    <tbody>
                      {clients.map((c) => (
                        <tr key={c.id} className="border-b border-navy/5 hover:bg-offwhite" data-testid={`client-row-${c.id}`}>
                          <td className="p-4 font-semibold text-navy">{c.name}</td>
                          <td className="p-4 text-navy/70">{c.email || "—"}</td>
                          <td className="p-4 text-navy/70">{c.phone || "—"}</td>
                          <td className="p-4 text-navy/70">{c.company || "—"}</td>
                          <td className="p-4 text-right"><button onClick={() => delClient(c.id)} data-testid={`del-client-${c.id}`} className="text-navy/30 hover:text-red-500 transition-colors"><Trash2 size={16} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </TabsContent>

          {/* SEND CAMPAIGN */}
          <TabsContent value="send" className="mt-6">
            <form onSubmit={sendCampaign} className="bg-white border border-navy/10 p-8 max-w-3xl space-y-5" data-testid="campaign-form">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-display font-black text-2xl text-navy tracking-tight">Broadcast an Offer</div>
                  <p className="text-navy/50 text-sm mt-1 font-mono">
                    {selectedClients.length ? `Sending to ${selectedClients.length} selected client${selectedClients.length === 1 ? "" : "s"}.` : `Emails all ${clients.length} client${clients.length === 1 ? "" : "s"} in your list.`}
                  </p>
                </div>
                <button type="button" onClick={() => setCamp(PRICE_TEMPLATE)} data-testid="load-template-btn"
                  className="shrink-0 border border-navy/20 text-navy font-mono text-xs uppercase tracking-widest px-4 py-2 hover:border-emerald hover:text-emerald transition-colors">
                  Use Price-List Template
                </button>
              </div>

              {/* Saved templates */}
              <div className="border border-navy/10 p-4" data-testid="template-library">
                <div className="font-mono text-xs uppercase tracking-widest text-navy/50 mb-3">Template Library</div>
                {templates.length === 0 ? (
                  <p className="text-navy/40 font-mono text-xs">No saved templates yet. Compose a message below and save it for reuse.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center border border-navy/15" data-testid={`template-${t.id}`}>
                        <button type="button" onClick={() => setCamp({ subject: t.subject, body: t.body })} data-testid={`load-saved-${t.id}`}
                          className="px-3 py-2 text-navy text-sm hover:bg-emerald hover:text-navy transition-colors">{t.name}</button>
                        <button type="button" onClick={() => delTemplate(t.id)} data-testid={`del-template-${t.id}`}
                          className="px-2 py-2 text-navy/30 hover:text-red-500 border-l border-navy/15"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Name this template" data-testid="template-name"
                    className="flex-1 bg-white border border-navy/20 px-3 py-2 text-navy text-sm placeholder:text-navy/40 focus:outline-none focus:border-emerald" />
                  <button type="button" onClick={saveTemplate} data-testid="save-template-btn"
                    className="flex items-center gap-2 bg-navy text-white font-mono text-xs uppercase tracking-widest px-4 py-2 hover:bg-emerald hover:text-navy transition-colors">
                    <Save size={14} /> Save Current
                  </button>
                </div>
              </div>

              <input className={inputCls} placeholder="Email subject *" value={camp.subject} onChange={(e) => setCamp({ ...camp, subject: e.target.value })} data-testid="campaign-subject" />
              <textarea className={`${inputCls} min-h-[220px] resize-none`} placeholder="Write your offer message here...&#10;&#10;e.g. This week we are paying premium rates for clean HDPE regrind. FTL & FCL welcome." value={camp.body} onChange={(e) => setCamp({ ...camp, body: e.target.value })} data-testid="campaign-body" />

              {/* Recipient targeting */}
              <div className="border border-navy/10">
                <div className="flex items-center justify-between px-4 py-3 border-b border-navy/10 bg-offwhite">
                  <span className="font-mono text-xs uppercase tracking-widest text-navy/60">Recipients</span>
                  <button type="button" onClick={toggleAll} data-testid="toggle-all-clients" className="font-mono text-xs uppercase tracking-widest text-emerald hover:underline">
                    {allSelected ? "Clear selection (send to all)" : "Select all"}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-navy/5">
                  {clients.length === 0 ? <p className="p-4 text-navy/40 font-mono text-sm">No clients yet. Add clients first.</p> :
                    clients.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-offwhite" data-testid={`select-client-${c.id}`}>
                        <input type="checkbox" checked={selectedClients.includes(c.id)} onChange={() => toggleClient(c.id)} className="accent-emerald w-4 h-4" />
                        <span className="text-navy text-sm font-semibold">{c.name}</span>
                        <span className="text-navy/50 text-sm">{c.email || c.phone}</span>
                      </label>
                    ))}
                </div>
                <p className="px-4 py-2 text-navy/40 font-mono text-[11px] border-t border-navy/10">Leave all unchecked to send to your entire list.</p>
              </div>

              <button type="submit" disabled={sending} data-testid="campaign-send-btn"
                className="bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-8 py-4 flex items-center gap-2 hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
                <Send size={16} /> {sending ? "Sending..." : selectedClients.length ? `Send to ${selectedClients.length} Selected` : "Send to All Clients"}
              </button>

              {/* WhatsApp manual send */}
              <div className="border border-navy/10 mt-2" data-testid="whatsapp-panel">
                <div className="px-4 py-3 border-b border-navy/10 bg-offwhite flex items-center gap-2">
                  <MessageCircle size={16} className="text-emerald" />
                  <span className="font-mono text-xs uppercase tracking-widest text-navy/60">Or send on WhatsApp (manual)</span>
                </div>
                <div className="p-4">
                  {(() => {
                    const list = clients.filter((c) => c.phone && (selectedClients.length === 0 || selectedClients.includes(c.id)));
                    if (!camp.subject && !camp.body) return <p className="text-navy/40 font-mono text-xs">Write a subject/message above, then tap a client to open WhatsApp with it pre-filled.</p>;
                    if (list.length === 0) return <p className="text-navy/40 font-mono text-xs">No clients with a phone number{selectedClients.length ? " in your selection" : ""}. Add phone numbers in the Clients tab.</p>;
                    const text = encodeURIComponent(`${camp.subject}\n\n${camp.body}`);
                    return (
                      <div className="flex flex-wrap gap-2">
                        {list.map((c) => (
                          <a key={c.id} href={`https://wa.me/${(c.phone || "").replace(/\D/g, "")}?text=${text}`} target="_blank" rel="noopener noreferrer"
                            data-testid={`whatsapp-send-${c.id}`}
                            className="flex items-center gap-2 border border-emerald text-navy font-mono text-xs px-3 py-2 hover:bg-emerald transition-colors">
                            <MessageCircle size={13} className="text-emerald" /> {c.name}
                          </a>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </form>
          </TabsContent>

          {/* HISTORY */}
          <TabsContent value="history" className="mt-6">
            <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="history-table">
              {campaigns.length === 0 ? <p className="p-8 text-navy/50 font-mono text-sm">No campaigns sent yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-navy/10 text-left font-mono text-xs uppercase tracking-widest text-navy/50">
                    <th className="p-4">Subject</th><th className="p-4">Recipients</th><th className="p-4">Sent</th><th className="p-4">Failed</th><th className="p-4">Date</th>
                  </tr></thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.id} className="border-b border-navy/5 hover:bg-offwhite" data-testid={`campaign-row-${c.id}`}>
                        <td className="p-4 font-semibold text-navy">{c.subject}</td>
                        <td className="p-4 text-navy/70">{c.recipients}</td>
                        <td className="p-4"><Badge className="rounded-none bg-emerald/10 text-emerald hover:bg-emerald/10 border-0">{c.sent}</Badge></td>
                        <td className="p-4 text-navy/70">{c.failed}</td>
                        <td className="p-4 text-navy/50 font-mono text-xs">{new Date(c.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* INSIGHTS */}
          <TabsContent value="insights" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-navy/10 p-6" data-testid="insights-materials">
                <div className="flex items-center gap-2 mb-6">
                  <BarChart3 size={18} className="text-emerald" />
                  <span className="font-mono text-xs uppercase tracking-widest text-navy/60">Offers by Material</span>
                </div>
                {analytics.by_material.length === 0 ? (
                  <p className="text-navy/40 font-mono text-sm py-12 text-center">No offer data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={analytics.by_material} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
                      <XAxis dataKey="material" tick={{ fontSize: 11, fill: "#64748B" }} angle={-25} textAnchor="end" interval={0} height={70} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <Tooltip cursor={{ fill: "rgba(16,185,129,0.08)" }} contentStyle={{ border: "1px solid #0A2540", borderRadius: 0, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#0A2540" radius={[0, 0, 0, 0]}>
                        {analytics.by_material.map((_, i) => <Cell key={i} fill={i === 0 ? "#10B981" : "#0A2540"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="bg-white border border-navy/10 p-6" data-testid="insights-status">
                <span className="font-mono text-xs uppercase tracking-widest text-navy/60">Offers by Status</span>
                <div className="mt-6 space-y-3">
                  {analytics.by_status.length === 0 ? <p className="text-navy/40 font-mono text-sm">No data yet.</p> :
                    analytics.by_status.map((s) => (
                      <div key={s.status} className="flex items-center justify-between border-b border-navy/5 pb-3" data-testid={`status-stat-${s.status}`}>
                        <span className={`font-mono text-xs uppercase tracking-wide px-2 py-1 ${STATUS_STYLES[s.status] || STATUS_STYLES.new}`}>{s.status}</span>
                        <span className="font-display font-black text-2xl text-navy">{s.count}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* QUOTES AUDIT */}
          <TabsContent value="quotes" className="mt-6">
            <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="quotes-table">
              {quotes.length === 0 ? <p className="p-8 text-navy/50 font-mono text-sm">No quotes sent yet. Use the Quote button on an offer to send one.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-navy/10 text-left font-mono text-xs uppercase tracking-widest text-navy/50">
                    <th className="p-4">Company</th><th className="p-4">Material</th><th className="p-4">Price</th><th className="p-4">Delivery</th><th className="p-4">Date</th><th className="p-4">Action</th>
                  </tr></thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.id} className="border-b border-navy/5 hover:bg-offwhite" data-testid={`quote-row-${q.id}`}>
                        <td className="p-4 font-semibold text-navy">{q.company_name}<div className="font-normal text-navy/50 text-xs">{q.email}</div></td>
                        <td className="p-4 text-navy/70">{q.material_type}</td>
                        <td className="p-4 font-semibold text-navy">{q.price}</td>
                        <td className="p-4">
                          {q.email_sent
                            ? <Badge className="rounded-none bg-green-100 text-green-700 hover:bg-green-100 border-0">Delivered</Badge>
                            : <Badge className="rounded-none bg-red-100 text-red-700 hover:bg-red-100 border-0">Failed</Badge>}
                        </td>
                        <td className="p-4 text-navy/50 font-mono text-xs">{new Date(q.created_at).toLocaleString()}</td>
                        <td className="p-4">
                          {!q.email_sent && (
                            <button onClick={() => retryQuote(q.id)} data-testid={`retry-quote-${q.id}`}
                              className="flex items-center gap-1 border border-navy/20 text-navy font-mono text-xs uppercase tracking-wide px-3 py-1.5 hover:bg-emerald hover:border-emerald transition-colors">
                              <RefreshCw size={12} /> Retry
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {editListing && (
        <div className="fixed inset-0 z-[60] bg-navy/80 flex items-center justify-center p-4 md:p-6" onClick={() => setEditListing(null)} data-testid="edit-listing-modal">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-emerald">Edit Listing</div>
                <div className="font-display font-black text-xl text-navy tracking-tight mt-1">{editListing.title || "Untitled"}</div>
              </div>
              <button onClick={() => setEditListing(null)} data-testid="close-edit-listing" className="text-navy/40 hover:text-navy"><X size={22} /></button>
            </div>
            <form onSubmit={saveEditListing} className="p-6 space-y-4" data-testid="edit-listing-form">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-navy/50">Status</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { v: "open",   l: "Open",   c: "bg-green-100 text-green-700 border-green-300", a: "bg-green-600 text-white border-green-600" },
                    { v: "sold",   l: "Sold",   c: "bg-navy/5 text-navy/60 border-navy/20",       a: "bg-navy text-white border-navy" },
                    { v: "closed", l: "Closed", c: "bg-navy/5 text-navy/60 border-navy/20",       a: "bg-navy/70 text-white border-navy/70" },
                  ].map((s) => (
                    <button type="button" key={s.v} onClick={() => setEditListing((p) => ({ ...p, status: s.v }))}
                      data-testid={`edit-status-${s.v}`}
                      className={`font-mono text-xs uppercase tracking-widest px-3 py-2 border ${editListing.status === s.v ? s.a : s.c}`}>
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>
              <input className={inputCls} placeholder="Title *" value={editListing.title || ""} onChange={(e) => setEditListing({ ...editListing, title: e.target.value })} data-testid="edit-title" />
              <input className={inputCls} placeholder="Material *" value={editListing.material_type || ""} onChange={(e) => setEditListing({ ...editListing, material_type: e.target.value })} data-testid="edit-material" />
              <textarea className={`${inputCls} min-h-[90px] resize-none`} placeholder="Description" value={editListing.description || ""} onChange={(e) => setEditListing({ ...editListing, description: e.target.value })} data-testid="edit-description" />
              <input className={inputCls} placeholder="Quantity available" value={editListing.quantity || ""} onChange={(e) => setEditListing({ ...editListing, quantity: e.target.value })} data-testid="edit-quantity" />
              <input className={inputCls} placeholder="Location" value={editListing.location || ""} onChange={(e) => setEditListing({ ...editListing, location: e.target.value })} data-testid="edit-location" />
              {(editListing.images?.length || 0) > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-navy/50 mb-2">Photos ({editListing.images.length})</div>
                  <div className="grid grid-cols-4 gap-2" data-testid="edit-image-previews">
                    {editListing.images.map((src, i) => (
                      <div key={i} className="relative aspect-square border border-navy/10">
                        <img src={src} alt={`img-${i}`} className="w-full h-full object-cover" />
                        {i === 0 && <span className="absolute top-1 left-1 bg-emerald text-navy font-mono text-[9px] px-1.5 py-0.5 tracking-widest">MAIN</span>}
                        <button type="button" onClick={() => removeEditImage(i)}
                          data-testid={`remove-edit-image-${i}`}
                          className="absolute -top-2 -right-2 bg-navy text-white w-5 h-5 flex items-center justify-center hover:bg-red-500 transition-colors">
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-navy/50 mt-2 font-mono">First photo is the main image shown on the public /offers card.</div>
                </div>
              )}
              <label className="flex items-center gap-2 border border-dashed border-navy/30 px-4 py-3 cursor-pointer hover:border-emerald transition-colors text-navy/70 text-sm">
                <Upload size={16} className="text-emerald" /> {(editListing.images?.length || 0) > 0 ? "Add more photos" : "Add photos (up to 8)"}
                <input type="file" accept="image/*" multiple onChange={onEditListingImage} className="hidden" data-testid="edit-image-input" />
              </label>
              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={savingEdit} data-testid="save-edit-listing"
                  className="flex-1 bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
                <button type="button" onClick={() => setEditListing(null)} data-testid="cancel-edit-listing"
                  className="bg-navy/5 text-navy font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-navy/10 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {bidsView && (
        <div className="fixed inset-0 z-[60] bg-navy/80 flex items-center justify-center p-6" onClick={() => setBidsView(null)} data-testid="bids-modal">
          <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-emerald">Bids Received</div>
                <div className="font-display font-black text-xl text-navy tracking-tight mt-1">{bidsView.listing.title}</div>
              </div>
              <button onClick={() => setBidsView(null)} data-testid="close-bids" className="text-navy/40 hover:text-navy"><X size={22} /></button>
            </div>
            <div className="p-6">
              {bidsView.bids.length === 0 ? <p className="text-navy/50 font-mono text-sm">No bids on this listing yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-navy/10 text-left font-mono text-xs uppercase tracking-widest text-navy/50">
                    <th className="p-3">Bidder</th><th className="p-3">Bid Price</th><th className="p-3">Qty</th><th className="p-3">Contact</th><th className="p-3">Date</th>
                  </tr></thead>
                  <tbody>
                    {bidsView.bids.map((b) => (
                      <tr key={b.id} className="border-b border-navy/5" data-testid={`bid-row-${b.id}`}>
                        <td className="p-3 font-semibold text-navy">{b.name}<div className="font-normal text-navy/40 text-xs">{b.company}</div></td>
                        <td className="p-3 font-bold text-emerald">{b.price}</td>
                        <td className="p-3 text-navy/70">{b.quantity || "—"}</td>
                        <td className="p-3 text-navy/70 text-xs">{b.email}<div>{b.phone}</div></td>
                        <td className="p-3 text-navy/50 font-mono text-xs">{new Date(b.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {quote && (
        <div className="fixed inset-0 z-[60] bg-navy/80 flex items-center justify-center p-6" onClick={() => setQuote(null)} data-testid="quote-modal">
          <div className="bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10">
              <div>
                <div className="font-display font-black text-xl text-navy tracking-tight">Send Quote</div>
                <div className="font-mono text-xs text-navy/50">{quote.offer.company_name} · {quote.offer.material_type} · {quote.offer.quantity}</div>
              </div>
              <button onClick={() => setQuote(null)} data-testid="close-quote" className="text-navy/40 hover:text-navy"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-navy/50">Offer price *</label>
                <input value={quote.price} onChange={(e) => setQuote({ ...quote, price: e.target.value })} placeholder="e.g. $0.45 / lb or $900 / MT"
                  data-testid="quote-price" className={`${inputCls} mt-2`} />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-navy/50">Message (optional)</label>
                <textarea value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} placeholder="Terms, pickup notes, conditions..."
                  data-testid="quote-message" className={`${inputCls} mt-2 min-h-[110px] resize-none`} />
              </div>
              <p className="font-mono text-[11px] text-navy/40">This emails the quote to {quote.offer.email} and marks the offer as quoted.</p>
              <button onClick={sendQuote} disabled={quoting} data-testid="send-quote-btn"
                className="w-full bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-6 py-4 flex items-center justify-center gap-2 hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
                <Send size={16} /> {quoting ? "Sending..." : "Send Quote"}
              </button>
            </div>
          </div>
        </div>
      )}

      {attView && (
        <div className="fixed inset-0 z-[60] bg-navy/80 flex items-center justify-center p-6" onClick={() => setAttView(null)} data-testid="attachments-modal">
          <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white">
              <div>
                <div className="font-display font-black text-xl text-navy tracking-tight">Photos — {attView.offer.company_name}</div>
                <div className="font-mono text-xs text-navy/50">{attView.offer.material_type}</div>
              </div>
              <button onClick={() => setAttView(null)} data-testid="close-attachments" className="text-navy/40 hover:text-navy"><X size={22} /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              {attView.images.length === 0 ? <p className="text-navy/50 font-mono text-sm">No photos.</p> :
                attView.images.map((img) => (
                  <a key={img.id} href={img.data_url} download={img.filename} className="block border border-navy/10 group">
                    <img src={img.data_url} alt={img.filename} className="w-full h-56 object-cover" />
                    <div className="px-3 py-2 font-mono text-xs text-navy/60 flex items-center justify-between">
                      <span className="truncate">{img.filename}</span><Download size={14} className="text-emerald" />
                    </div>
                  </a>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(!!getAdminKey());
  const logout = () => { clearAdminKey(); setAuthed(false); };
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
};

export default Admin;
